import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Copy, Pencil, Archive, LogOut, Loader2, CalendarDays, Users, DollarSign,
  Ticket, ArrowLeft, Search, Tag, Trash2, Type, Save, ImagePlus, Images,
  Clapperboard, Star,
} from "lucide-react";
import { api, formatApiError, formatDate, money, THEME_IMAGES } from "../lib/api";
import { DEFAULTS } from "../lib/content";
import { MASLogo } from "../components/MASLogo";

const GUEST_COLORS = [
  "from-[#3d3833] to-[#000000]",
  "from-[#3b82f6] to-[#1d4ed8]",
  "from-[#10b981] to-[#047857]",
  "from-[#a855f7] to-[#7e22ce]",
  "from-[#f59e0b] to-[#d97706]",
  "from-[#ec4899] to-[#be185d]",
];

const emptyForm = {
  title: "", theme: "Paint & Sip", description: "", price: "35", date: "",
  image: "", media: [], featured: false, recurring: "none", repeat_count: "0",
  timeslots: [{ id: null, label: "6:30 PM", capacity: "5" }],
};

const CONTENT_SECTIONS = [
  { title: "Page photos", fields: [
    { key: "hero_image", label: "Hero background (the big art photo)", image: true },
    { key: "pottery_image", label: "Pottery block — small photo", image: true },
    { key: "lounge_space_image", label: "Lounge — big photo card", image: true },
    { key: "lounge_art_image", label: "Lounge — art/neon card", image: true },
    { key: "lounge_snacks_image", label: "Lounge — snacks card", image: true },
  ]},
  { title: "Hero (top of page)", fields: [
    { key: "about_kicker", label: "Small label above headline" },
    { key: "hero_badge", label: "Badge text (after the label)" },
    { key: "hero_title_1", label: "Headline — line 1 (white)" },
    { key: "hero_title_2", label: "Headline — line 2 (yellow)" },
    { key: "hero_subtitle", label: "About paragraph", multi: true },
    { key: "hero_meta", label: "Small info line under paragraph" },
  ]},
  { title: "Workshops section", fields: [
    { key: "schedule_kicker", label: "Small italic word under heading" },
    { key: "schedule_heading", label: "Heading" },
  ]},
  { title: "Gallery section", fields: [
    { key: "gallery_kicker", label: "Small label above mosaic" },
    { key: "gallery_heading", label: "Big word under mosaic" },
  ]},
  { title: "Studio videos section", fields: [
    { key: "videos_kicker", label: "Small red label above heading" },
    { key: "videos_heading", label: "Heading" },
  ]},
  { title: "The Lounge section", fields: [
    { key: "lounge_kicker", label: "Small italic word" },
    { key: "lounge_heading", label: "Heading", multi: true },
    { key: "space_title", label: "Big photo card — title" },
    { key: "space_text", label: "Big photo card — text", multi: true },
    { key: "neon_title", label: "Art card — title" },
    { key: "neon_text", label: "Art card — text", multi: true },
    { key: "snacks_title", label: "Snacks card — title" },
    { key: "snacks_text", label: "Snacks card — text", multi: true },
  ]},
  { title: "Feature cards (4)", fields: [
    { key: "feature_1_title", label: "Card 1 title" }, { key: "feature_1_text", label: "Card 1 text", multi: true },
    { key: "feature_2_title", label: "Card 2 title" }, { key: "feature_2_text", label: "Card 2 text", multi: true },
    { key: "feature_3_title", label: "Card 3 title" }, { key: "feature_3_text", label: "Card 3 text", multi: true },
    { key: "feature_4_title", label: "Card 4 title" }, { key: "feature_4_text", label: "Card 4 text", multi: true },
  ]},
  { title: "FAQ (4 questions)", fields: [
    { key: "faq_kicker", label: "Small label" },
    { key: "faq_heading", label: "Heading" },
    { key: "faq_1_q", label: "Question 1" }, { key: "faq_1_a", label: "Answer 1", multi: true },
    { key: "faq_2_q", label: "Question 2" }, { key: "faq_2_a", label: "Answer 2", multi: true },
    { key: "faq_3_q", label: "Question 3" }, { key: "faq_3_a", label: "Answer 3", multi: true },
    { key: "faq_4_q", label: "Question 4" }, { key: "faq_4_a", label: "Answer 4", multi: true },
  ]},
  { title: "Pottery block", fields: [
    { key: "pottery_kicker", label: "Small italic word" },
    { key: "pottery_heading", label: "Heading" },
    { key: "pottery_text", label: "Text", multi: true },
    { key: "pottery_button", label: "Button label" },
  ]},
  { title: "Visit / footer", fields: [
    { key: "visit_heading", label: "Heading", multi: true },
    { key: "visit_text", label: "Text", multi: true },
  ]},
  { title: "Policies", fields: [
    { key: "policies_kicker", label: "Small italic word" },
    { key: "policies_heading", label: "Heading" },
    { key: "policy_1_title", label: "Policy 1 title" }, { key: "policy_1_text", label: "Policy 1 text", multi: true },
    { key: "policy_2_title", label: "Policy 2 title" }, { key: "policy_2_text", label: "Policy 2 text", multi: true },
    { key: "policy_3_title", label: "Policy 3 title" }, { key: "policy_3_text", label: "Policy 3 text", multi: true },
    { key: "policy_4_title", label: "Policy 4 title" }, { key: "policy_4_text", label: "Policy 4 text", multi: true },
  ]},
  { title: "After purchase (ticket card)", fields: [
    { key: "prepare_title", label: "Checklist title" },
    { key: "prepare_items", label: "Checklist items (one per line)", multi: true, rows: 6 },
  ]},
  { title: "Terms & Conditions", fields: [
    { key: "terms_heading", label: "Heading" },
    { key: "terms_text", label: "Full text (blank lines = new paragraphs)", multi: true, rows: 14 },
  ]},
];

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const [tab, setTab] = useState("workshops");
  const [workshops, setWorkshops] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [promos, setPromos] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const [seatWorkshopId, setSeatWorkshopId] = useState("");
  const [seating, setSeating] = useState(null);
  const [seatingLoading, setSeatingLoading] = useState(false);

  const [promoForm, setPromoForm] = useState({ code: "", discount_type: "percent", value: "10", max_uses: "0" });
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");

  const [categories, setCategories] = useState([]);
  const [catName, setCatName] = useState("");
  const [catBusy, setCatBusy] = useState(false);
  const [catEdit, setCatEdit] = useState({});
  const [catEditBusy, setCatEditBusy] = useState("");

  const addCategory = async (e) => {
    e.preventDefault();
    if (catName.trim().length < 2) return;
    setCatBusy(true);
    try {
      await api.post("/admin/categories", { name: catName.trim() });
      setCatName("");
      showToast("Category added");
      await loadData();
    } catch (err) {
      showToast(formatApiError(err, "Couldn't add that category."));
    } finally {
      setCatBusy(false);
    }
  };

  const renameCategory = async (id) => {
    const name = (catEdit[id] || "").trim();
    if (name.length < 2) return;
    setCatEditBusy(id);
    try {
      await api.put(`/admin/categories/${id}`, { name });
      setCatEdit((m) => ({ ...m, [id]: undefined }));
      showToast("Category renamed — workshops updated too");
      await loadData();
    } catch (err) {
      showToast(formatApiError(err, "Couldn't rename that category."));
    } finally {
      setCatEditBusy("");
    }
  };

  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Delete the "${name}" category? Workshops in it stay on the site under "All".`)) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      showToast("Category deleted");
      await loadData();
    } catch (err) {
      showToast(formatApiError(err, "Couldn't delete that category."));
    }
  };

  const [contentDraft, setContentDraft] = useState(null);
  const [contentBusy, setContentBusy] = useState(false);

  const [addGuest, setAddGuest] = useState({});
  const [guestBusy, setGuestBusy] = useState("");

  const addManualGuest = async (workshopId, slotId) => {
    const entry = addGuest[slotId] || {};
    if (!entry.name || entry.name.trim().length < 2) {
      showToast("Enter a name first");
      return;
    }
    setGuestBusy(slotId);
    try {
      await api.post(`/admin/seating/${workshopId}/guests`, {
        timeslot_id: slotId,
        name: entry.name,
        seats: parseInt(entry.seats || "1", 10),
      });
      setAddGuest((m) => ({ ...m, [slotId]: { name: "", seats: "1" } }));
      showToast("Guest added to the seating chart");
      await loadSeating(workshopId);
      await loadData();
    } catch (err) {
      showToast(formatApiError(err, "Couldn't add that guest."));
    } finally {
      setGuestBusy("");
    }
  };

  const removeGuest = async (workshopId, bookingId, name) => {
    if (!window.confirm(`Remove ${name} from this timeslot?`)) return;
    try {
      await api.post(`/admin/seating/remove-guest/${bookingId}`);
      showToast("Guest removed");
      await loadSeating(workshopId);
      await loadData();
    } catch (err) {
      showToast(formatApiError(err, "Couldn't remove that guest."));
    }
  };

  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef(null);
  const [videos, setVideos] = useState([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const videoInputRef = useRef(null);

  const uploadVideo = async (file) => {
    if (!file) return;
    setVideoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      showToast("Video added to the studio section");
      await loadData();
    } catch (err) {
      showToast(formatApiError(err, "Couldn't upload that video."));
    } finally {
      setVideoBusy(false);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const deleteVideo = async (v) => {
    if (!window.confirm("Remove this video from the site?")) return;
    try {
      await api.delete(`/admin/videos/${v.id}`);
      await loadData();
    } catch (err) {
      showToast(formatApiError(err));
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3200);
  };

  const loadData = useCallback(async () => {
    const [ws, bk, st, pr, ct, gl, cg, vd] = await Promise.all([
      api.get("/workshops", { params: { include_inactive: true } }),
      api.get("/admin/bookings"),
      api.get("/admin/stats"),
      api.get("/admin/promos"),
      api.get("/admin/content"),
      api.get("/gallery"),
      api.get("/categories"),
      api.get("/videos"),
    ]);
    setWorkshops(ws.data);
    setBookings(bk.data);
    setStats(st.data);
    setPromos(pr.data);
    setContentDraft({ ...DEFAULTS, ...(ct.data || {}) });
    setGallery(gl.data || []);
    setCategories(cg.data || []);
    setVideos(vd.data || []);
    setSeatWorkshopId((cur) => cur || (ws.data.find((w) => w.active) || ws.data[0])?._id || "");
  }, []);

  const loadSeating = useCallback(async (id) => {
    if (!id) return;
    setSeatingLoading(true);
    try {
      const { data } = await api.get(`/admin/seating/${id}`);
      setSeating(data);
    } catch (e) {
      showToast(formatApiError(e));
    } finally {
      setSeatingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "seating" && seatWorkshopId) loadSeating(seatWorkshopId);
  }, [tab, seatWorkshopId, loadSeating]);

  useEffect(() => {
    (async () => {
      if (!localStorage.getItem("mas_admin_token")) {
        setChecking(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadData();
      } catch {
        localStorage.removeItem("mas_admin_token");
      } finally {
        setChecking(false);
      }
    })();
  }, [loadData]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginBusy(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("mas_admin_token", data.token);
      setUser(data.user);
      await loadData();
    } catch (err) {
      setLoginError(formatApiError(err, "Login failed."));
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("mas_admin_token");
    setUser(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, theme: categories[0]?.name || emptyForm.theme });
    setFormError("");
    setFormOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      title: w.title, theme: w.theme, description: w.description,
      price: String(w.price_cents / 100), date: w.date,
      image: w.image,
      media: (w.media?.length ? w.media : (w.image ? [{ kind: "image", url: w.image }] : [])).map((m) => ({ kind: m.kind, url: m.url })),
      featured: !!w.featured,
      recurring: "none", repeat_count: "0",
      timeslots: (w.timeslots || []).map((s) => ({ id: s.id, label: s.label, capacity: String(s.capacity) })),
    });
    setFormError("");
    setFormOpen(true);
  };

  const setSlot = (i, key, val) =>
    setForm((f) => ({
      ...f,
      timeslots: f.timeslots.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)),
    }));

  const saveWorkshop = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormBusy(true);
    const firstImage = form.media.find((m) => m.kind === "image");
    const payload = {
      title: form.title,
      theme: form.theme,
      description: form.description,
      price_cents: Math.round(parseFloat(form.price || "0") * 100),
      date: form.date,
      timeslots: form.timeslots
        .filter((s) => s.label.trim())
        .map((s) => ({ id: s.id || undefined, label: s.label.trim(), capacity: parseInt(s.capacity || "1", 10) })),
      image: firstImage?.url || form.image || THEME_IMAGES[form.theme] || THEME_IMAGES.default,
      media: form.media.filter((m) => m.url),
      active: true,
      featured: form.featured,
      recurring: form.recurring === "none" ? null : form.recurring,
      repeat_count: parseInt(form.repeat_count || "0", 10),
    };
    try {
      if (editing) {
        await api.put(`/admin/workshops/${editing._id}`, payload);
        showToast("Workshop updated");
      } else {
        const { data } = await api.post("/admin/workshops", payload);
        showToast(
          data.series_created > 1
            ? `Series of ${data.series_created} workshops created`
            : "Workshop created"
        );
      }
      setFormOpen(false);
      await loadData();
    } catch (err) {
      setFormError(formatApiError(err, "Could not save workshop."));
    } finally {
      setFormBusy(false);
    }
  };

  const duplicate = async (w) => {
    try {
      await api.post(`/admin/workshops/${w._id}/duplicate`);
      showToast("Workshop duplicated — set its new date");
      await loadData();
    } catch (err) {
      showToast(formatApiError(err));
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Archive "${w.title}"? It will disappear from the public site.`)) return;
    try {
      const { data } = await api.delete(`/admin/workshops/${w._id}`);
      showToast(data.detail || "Workshop archived");
      await loadData();
    } catch (err) {
      showToast(formatApiError(err));
    }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    setPromoError("");
    setPromoBusy(true);
    try {
      await api.post("/admin/promos", {
        code: promoForm.code,
        discount_type: promoForm.discount_type,
        value: parseInt(promoForm.value || "0", 10),
        active: true,
        max_uses: parseInt(promoForm.max_uses || "0", 10),
      });
      showToast(`Promo ${promoForm.code.toUpperCase()} created`);
      setPromoForm({ code: "", discount_type: "percent", value: "10", max_uses: "0" });
      await loadData();
    } catch (err) {
      setPromoError(formatApiError(err, "Could not create promo."));
    } finally {
      setPromoBusy(false);
    }
  };

  const togglePromo = async (p) => {
    try {
      await api.put(`/admin/promos/${p._id}`, { active: !p.active });
      await loadData();
    } catch (err) {
      showToast(formatApiError(err));
    }
  };

  const saveContent = async () => {
    setContentBusy(true);
    try {
      await api.put("/admin/content", { data: contentDraft });
      showToast("Site text saved — refresh the site to see it live");
    } catch (err) {
      showToast(formatApiError(err, "Could not save site text."));
    } finally {
      setContentBusy(false);
    }
  };

  const uploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    setUploadBusy(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        showToast(formatApiError(err, `Couldn't upload ${file.name}`));
      }
    }
    if (ok > 0) showToast(`${ok} photo${ok > 1 ? "s" : ""} added to the gallery`);
    setUploadBusy(false);
    await loadData();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fullUrl = (u) =>
    u && u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;

  const uploadFieldImage = async (key, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContentDraft((d) => ({ ...d, [key]: fullUrl(data.url) }));
      showToast("Photo uploaded — remember to hit Save site text");
    } catch (err) {
      showToast(formatApiError(err, "Upload failed."));
    }
  };

  const uploadWorkshopImage = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: fullUrl(data.url) }));
      showToast("Cover photo uploaded");
    } catch (err) {
      showToast(formatApiError(err, "Upload failed."));
    }
  };

  const uploadWorkshopMedia = async (files) => {
    if (!files || files.length === 0) return;
    setMediaBusy(true);
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const kind = data.media_kind || (file.type.startsWith("video") ? "video" : "image");
        setForm((f) => (f.media.length >= 8 ? f : { ...f, media: [...f.media, { kind, url: fullUrl(data.url) }] }));
      } catch (err) {
        showToast(formatApiError(err, `Couldn't upload ${file.name}`));
      }
    }
    setMediaBusy(false);
  };

  const setMediaCover = (i) =>
    setForm((f) => {
      const media = [...f.media];
      const [item] = media.splice(i, 1);
      media.unshift(item);
      return { ...f, media };
    });

  const removeMedia = (i) =>
    setForm((f) => ({ ...f, media: f.media.filter((_, idx) => idx !== i) }));

  const deletePhoto = async (p) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${p.id}`);
      await loadData();
    } catch (err) {
      showToast(formatApiError(err));
    }
  };

  const filteredBookings = bookings.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [b.guest_name, b.guest_email, b.workshop_title, b.booking_code]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  const workshopSeats = (w) => (w.timeslots || []).reduce((a, s) => a + s.capacity, 0);
  const workshopBooked = (w) => (w.timeslots || []).reduce((a, s) => a + s.booked, 0);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#eae4da]">
        <Loader2 className="animate-spin text-[#1d1d1b]" size={32} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#eae4da] px-6">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={doLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="card-soft w-full max-w-sm p-8"
        >
          <MASLogo size={44} />
          <h1 className="font-display font-extrabold text-2xl tracking-tight mt-5">Studio Login</h1>
          <p className="font-editorial text-neutral-500 mt-1">Owner access to MAS Arts workshops.</p>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            placeholder="Email"
            value={loginForm.email}
            onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
            className="mt-6 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1d1d1b] placeholder:text-neutral-300"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            placeholder="Password"
            value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            className="mt-3 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1d1d1b] placeholder:text-neutral-300"
          />
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-600">{loginError}</p>
          )}
          <button
            data-testid="admin-login-submit"
            disabled={loginBusy}
            className="btn-primary mt-6 w-full rounded-full disabled:opacity-60 py-3.5 font-bold flex items-center justify-center gap-2"
          >
            {loginBusy && <Loader2 size={15} className="animate-spin" />}
            Sign in
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-4 w-full text-xs font-semibold text-neutral-400 hover:text-black transition-colors"
          >
            ← Back to the site
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eae4da]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#eae4da]/85 backdrop-blur-2xl border-b border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="leading-none">
            <span className="font-display font-extrabold tracking-tight text-lg">Studio Panel</span>
            <span className="block text-[10px] font-semibold tracking-[0.18em] text-neutral-400 uppercase mt-1">
              {user.email}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-back-to-site"
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border border-neutral-300 bg-white hover:border-black transition-colors"
            >
              <ArrowLeft size={14} /> Site
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border border-neutral-300 bg-white hover:border-red-500 hover:text-red-600 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: DollarSign, label: "Revenue", value: money(stats.revenue_cents), testId: "stat-revenue" },
              { icon: Ticket, label: "Paid bookings", value: stats.paid_bookings, testId: "stat-bookings" },
              { icon: Users, label: "Seats sold", value: stats.seats_sold, testId: "stat-seats" },
              { icon: CalendarDays, label: "Active workshops", value: stats.active_workshops, testId: "stat-workshops" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testId} className="card-soft p-5">
                <s.icon size={18} className="text-[#1d1d1b]" />
                <p className="font-display font-extrabold text-2xl tracking-tight mt-3">{s.value}</p>
                <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-2 flex-wrap">
            {["workshops", "seating", "bookings", "promos", "gallery", "videos", "categories", "content"].map((tb) => (
              <button
                key={tb}
                data-testid={`admin-tab-${tb}`}
                onClick={() => setTab(tb)}
                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-300 ${
                  tab === tb
                    ? "bg-[#1d1d1b] text-[#eae4da] shadow-[0_10px_24px_-10px_rgba(0,0,0,0.4)]"
                    : "bg-white border border-black/[0.08] text-neutral-500 hover:text-black"
                }`}
              >
                {tb === "content" ? "Site Text" : tb[0].toUpperCase() + tb.slice(1)}
              </button>
            ))}
          </div>
          {tab === "workshops" && (
            <button
              data-testid="admin-add-workshop-button"
              onClick={openCreate}
              className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"
            >
              <Plus size={15} /> New workshop
            </button>
          )}
          {tab === "bookings" && (
            <div className="relative">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="admin-bookings-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search guests, codes, workshops…"
                className="rounded-full border border-neutral-200 bg-white pl-10 pr-5 py-2.5 text-sm outline-none focus:border-[#1d1d1b] w-72 placeholder:text-neutral-300"
              />
            </div>
          )}
        </div>

        {tab === "workshops" && (
          <div className="mt-6 grid gap-4">
            {workshops.length === 0 && (
              <p className="text-neutral-400 text-sm py-10 text-center font-medium">No workshops yet — create your first one.</p>
            )}
            {workshops.map((w) => (
              <motion.div
                key={w._id}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`admin-workshop-row-${w._id}`}
                className={`card-soft flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5 ${!w.active ? "opacity-55" : ""}`}
              >
                <img src={w.image} alt={w.title} className="w-full sm:w-24 h-28 sm:h-16 object-cover rounded-2xl shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-extrabold tracking-tight truncate">{w.title}</h3>
                    {!w.active && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-red-600 bg-red-50 border border-red-100 rounded-full px-2 py-0.5">
                        archived
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-neutral-500 mt-1">
                    {formatDate(w.date)} · {money(w.price_cents)} · {workshopBooked(w)}/{workshopSeats(w)} booked
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(w.timeslots || []).map((s) => (
                      <span key={s.id} className="rounded-full bg-[#eae4da] px-2.5 py-0.5 text-[11px] font-bold text-neutral-600">
                        {s.label} · {s.booked}/{s.capacity}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`admin-edit-workshop-button-${w._id}`}
                    onClick={() => openEdit(w)}
                    className="w-10 h-10 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-black transition-colors"
                    title="Edit"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    data-testid={`admin-duplicate-workshop-button-${w._id}`}
                    onClick={() => duplicate(w)}
                    className="w-10 h-10 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-black transition-colors"
                    title="Duplicate"
                  >
                    <Copy size={15} />
                  </button>
                  <button
                    data-testid={`admin-delete-workshop-button-${w._id}`}
                    onClick={() => remove(w)}
                    disabled={!w.active}
                    className="w-10 h-10 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-red-500 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Archive"
                  >
                    <Archive size={15} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {tab === "categories" && (
          <div data-testid="admin-categories-panel" className="mt-6 max-w-2xl">
            <form onSubmit={addCategory} className="card-soft p-5 flex items-center gap-3">
              <Tag size={16} className="text-neutral-400 shrink-0" />
              <input
                data-testid="category-add-input"
                value={catName}
                onChange={(e) => setCatName(e.target.value)}
                placeholder="New category name, e.g. Karaoke Night"
                maxLength={40}
                className="form-input flex-1"
              />
              <button
                type="submit"
                data-testid="category-add-button"
                disabled={catBusy || catName.trim().length < 2}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-40 shrink-0"
              >
                {catBusy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add
              </button>
            </form>
            <p className="text-xs text-neutral-400 font-medium mt-3 px-1">
              These become the filter buttons on the homepage and the choices in the workshop form. Renaming a category updates its workshops too; deleting one keeps its workshops under "All".
            </p>
            <div className="mt-4 grid gap-3">
              {categories.length === 0 && (
                <p className="text-neutral-400 text-sm py-8 text-center font-medium">No categories yet — add your first one above.</p>
              )}
              {categories.map((c) => {
                const inUse = workshops.filter((w) => w.theme === c.name && w.active).length;
                const editingThis = catEdit[c.id] !== undefined;
                return (
                  <div
                    key={c.id}
                    data-testid={`admin-category-row-${c.id}`}
                    className="card-soft flex items-center gap-3 p-4"
                  >
                    {editingThis ? (
                      <input
                        data-testid={`category-rename-input-${c.id}`}
                        autoFocus
                        value={catEdit[c.id]}
                        maxLength={40}
                        onChange={(e) => setCatEdit((m) => ({ ...m, [c.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); renameCategory(c.id); } if (e.key === "Escape") setCatEdit((m) => ({ ...m, [c.id]: undefined })); }}
                        className="form-input flex-1"
                      />
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-extrabold tracking-tight truncate">{c.name}</p>
                        <p className="text-xs font-medium text-neutral-500 mt-0.5">
                          {inUse} active workshop{inUse === 1 ? "" : "s"}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2 shrink-0">
                      {editingThis ? (
                        <>
                          <button
                            data-testid={`category-rename-save-${c.id}`}
                            onClick={() => renameCategory(c.id)}
                            disabled={catEditBusy === c.id || (catEdit[c.id] || "").trim().length < 2}
                            className="w-10 h-10 rounded-full bg-[#1d1d1b] text-[#eae4da] flex items-center justify-center hover:bg-black transition-colors disabled:opacity-40"
                            title="Save"
                          >
                            {catEditBusy === c.id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                          </button>
                          <button
                            data-testid={`category-rename-cancel-${c.id}`}
                            onClick={() => setCatEdit((m) => ({ ...m, [c.id]: undefined }))}
                            className="w-10 h-10 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-black transition-colors"
                            title="Cancel"
                          >
                            <ArrowLeft size={15} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            data-testid={`category-rename-button-${c.id}`}
                            onClick={() => setCatEdit((m) => ({ ...m, [c.id]: c.name }))}
                            className="w-10 h-10 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-black transition-colors"
                            title="Rename"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            data-testid={`category-delete-button-${c.id}`}
                            onClick={() => deleteCategory(c.id, c.name)}
                            className="w-10 h-10 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-red-500 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "seating" && (
          <div className="mt-6">
            <div className="flex items-center gap-3 flex-wrap">
              <select
                data-testid="seating-workshop-select"
                value={seatWorkshopId}
                onChange={(e) => setSeatWorkshopId(e.target.value)}
                className="rounded-full border border-neutral-200 bg-white px-5 py-3 text-sm font-bold outline-none focus:border-[#1d1d1b] max-w-full"
              >
                {workshops.filter((w) => w.active).map((w) => (
                  <option key={w._id} value={w._id}>
                    {w.title} — {formatDate(w.date)}
                  </option>
                ))}
              </select>
              {seatingLoading && <Loader2 size={18} className="animate-spin text-[#1d1d1b]" />}
            </div>

            {seating && !seatingLoading && (
              <div data-testid="seating-chart" className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {seating.timeslots.map((slot) => {
                  const seats = [];
                  slot.guests.forEach((g, gi) => {
                    for (let i = 0; i < g.seats; i++) seats.push({ guest: g, color: GUEST_COLORS[gi % GUEST_COLORS.length], first: i === 0 });
                  });
                  while (seats.length < slot.capacity) seats.push(null);
                  return (
                    <div key={slot.id} data-testid={`seating-timeslot-${slot.id}`} className="card-soft p-6">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display font-extrabold text-lg tracking-tight">{slot.label}</h3>
                        <span className={`text-xs font-bold rounded-full px-3 py-1 ${
                          slot.seats_left === 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                        }`}>
                          {slot.booked}/{slot.capacity} booked
                        </span>
                      </div>
                      <div className="mt-3 h-2 rounded-full bg-[#eae4da] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#1d1d1b] transition-all duration-700"
                          style={{ width: `${Math.min(100, (slot.booked / slot.capacity) * 100)}%` }}
                        />
                      </div>
                      <div className="mt-5 flex flex-wrap gap-2.5">
                        {seats.map((s, i) => (
                          <div
                            key={i}
                            data-testid={`seating-seat-${slot.id}-${i}`}
                            title={s ? `${s.guest.name} (${s.guest.code})` : "Open seat"}
                            className={`w-11 h-11 rounded-full flex items-center justify-center text-[11px] font-extrabold transition-all duration-300 ${
                              s
                                ? `bg-gradient-to-br ${s.color} text-white shadow-md`
                                : "border-2 border-dashed border-neutral-300 text-neutral-400"
                            }`}
                          >
                            {s
                              ? s.guest.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()
                              : i + 1}
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 space-y-2">
                        {slot.guests.length === 0 && (
                          <p className="text-xs font-medium text-neutral-400">No guests yet — all {slot.capacity} spots open.</p>
                        )}
                        {slot.guests.map((g, gi) => (
                          <div key={g.id || g.code} className="flex items-center gap-3 text-sm">
                            <span className={`w-6 h-6 rounded-full bg-gradient-to-br ${GUEST_COLORS[gi % GUEST_COLORS.length]} text-white flex items-center justify-center text-[9px] font-extrabold shrink-0`}>
                              {g.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                            </span>
                            <span className="font-bold truncate">{g.name}</span>
                            <span className="text-neutral-400 text-xs font-medium">×{g.seats}</span>
                            <span className={`ml-auto text-[10px] font-bold uppercase tracking-widest ${
                              g.status === "paid" ? "text-emerald-600" : g.status === "manual" ? "text-[#808bc5]" : "text-amber-500"
                            }`}>
                              {g.status === "manual" ? "added by you" : g.status}
                            </span>
                            <button
                              data-testid={`seating-remove-guest-${g.id}`}
                              onClick={() => removeGuest(seating.workshop_id, g.id, g.name)}
                              className="w-7 h-7 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-400 hover:border-red-400 hover:text-red-500 transition-colors shrink-0"
                              title={`Remove ${g.name}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 pt-4 border-t border-black/[0.06]">
                        <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 mb-2.5">
                          Add someone who didn't book online
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`seating-add-name-${slot.id}`}
                            value={(addGuest[slot.id] || {}).name || ""}
                            onChange={(e) => setAddGuest((m) => ({ ...m, [slot.id]: { ...(m[slot.id] || {}), name: e.target.value } }))}
                            placeholder="Guest name"
                            className="form-input flex-1 !py-2 text-xs"
                          />
                          <input
                            data-testid={`seating-add-seats-${slot.id}`}
                            type="number"
                            min="1"
                            value={(addGuest[slot.id] || {}).seats || "1"}
                            onChange={(e) => setAddGuest((m) => ({ ...m, [slot.id]: { ...(m[slot.id] || {}), seats: e.target.value } }))}
                            className="form-input !py-2 text-xs"
                            style={{ width: "4rem" }}
                          />
                          <button
                            data-testid={`seating-add-submit-${slot.id}`}
                            onClick={() => addManualGuest(seating.workshop_id, slot.id)}
                            disabled={guestBusy === slot.id}
                            className="w-9 h-9 rounded-full bg-[#1d1d1b] text-[#eae4da] flex items-center justify-center hover:bg-black transition-colors disabled:opacity-50 shrink-0"
                            title="Add guest"
                          >
                            {guestBusy === slot.id ? <Loader2 size={13} className="animate-spin" /> : <Plus size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings-table" className="mt-6 card-soft overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 border-b border-black/[0.06]">
                  <th className="px-5 py-4">Guest</th>
                  <th className="px-5 py-4">Workshop</th>
                  <th className="px-5 py-4">Code</th>
                  <th className="px-5 py-4">Seats</th>
                  <th className="px-5 py-4">Total</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Email</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-neutral-400 font-medium">
                      No bookings yet — they'll land here the moment a guest pays.
                    </td>
                  </tr>
                )}
                {filteredBookings.map((b) => (
                  <tr key={b._id} data-testid={`admin-booking-row-${b._id}`} className="border-b border-black/[0.04] hover:bg-black/[0.015] transition-colors">
                    <td className="px-5 py-4">
                      <p className="font-bold">{b.guest_name}</p>
                      <p className="text-xs text-neutral-400">{b.guest_email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold">{b.workshop_title}</p>
                      <p className="text-xs text-neutral-400">{formatDate(b.workshop_date)} · {b.timeslot_label || b.workshop_time}</p>
                    </td>
                    <td className="px-5 py-4 font-mono-mas text-xs">{b.booking_code}</td>
                    <td className="px-5 py-4 font-semibold">{b.seats}</td>
                    <td className="px-5 py-4 font-bold">
                      {money(b.total_cents)}
                      {b.promo_code && <span className="block text-[10px] font-bold text-emerald-600">{b.promo_code}</span>}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1 ${
                        b.status === "paid"
                          ? "text-emerald-700 bg-emerald-50"
                          : b.status === "pending"
                            ? "text-amber-600 bg-amber-50"
                            : "text-neutral-400 bg-neutral-100"
                      }`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-neutral-400 font-medium">
                      {(b.email_log || []).length > 0 ? b.email_log.join(" + ") + " notified" : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "promos" && (
          <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr]">
            <form data-testid="promo-form" onSubmit={createPromo} className="card-soft p-6 h-fit">
              <h3 className="font-display font-extrabold text-lg tracking-tight flex items-center gap-2">
                <Tag size={17} className="text-[#1d1d1b]" /> New promo code
              </h3>
              <input
                data-testid="promo-form-code"
                required
                minLength={2}
                value={promoForm.code}
                onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="CODE e.g. SUMMER20"
                className="mt-5 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm font-mono-mas tracking-wider uppercase outline-none focus:border-[#1d1d1b] placeholder:text-neutral-300"
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <select
                  data-testid="promo-form-type"
                  value={promoForm.discount_type}
                  onChange={(e) => setPromoForm((f) => ({ ...f, discount_type: e.target.value }))}
                  className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#1d1d1b]"
                >
                  <option value="percent">% off</option>
                  <option value="amount">$ off</option>
                </select>
                <input
                  data-testid="promo-form-value"
                  required
                  type="number"
                  min="1"
                  max={promoForm.discount_type === "percent" ? 100 : 1000}
                  value={promoForm.value}
                  onChange={(e) => setPromoForm((f) => ({ ...f, value: e.target.value }))}
                  placeholder={promoForm.discount_type === "percent" ? "10" : "5"}
                  className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1d1d1b]"
                />
              </div>
              <input
                data-testid="promo-form-max-uses"
                type="number"
                min="0"
                value={promoForm.max_uses}
                onChange={(e) => setPromoForm((f) => ({ ...f, max_uses: e.target.value }))}
                placeholder="Max uses (0 = unlimited)"
                className="mt-3 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1d1d1b] placeholder:text-neutral-300"
              />
              {promoError && <p className="mt-3 text-sm font-semibold text-red-600">{promoError}</p>}
              <button
                data-testid="promo-form-submit"
                disabled={promoBusy}
                className="btn-primary mt-5 w-full rounded-full py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {promoBusy && <Loader2 size={14} className="animate-spin" />}
                Create promo
              </button>
            </form>

            <div className="grid gap-3 content-start" data-testid="promo-list">
              {promos.length === 0 && (
                <p className="text-neutral-400 text-sm py-8 text-center font-medium">No promo codes yet.</p>
              )}
              {promos.map((p) => (
                <div key={p._id} data-testid={`promo-row-${p.code}`} className={`card-soft flex items-center gap-4 p-5 ${!p.active ? "opacity-55" : ""}`}>
                  <span className="rounded-xl bg-[#1d1d1b]/[0.07] text-[#1d1d1b] font-mono-mas font-bold text-sm px-4 py-2.5 tracking-wider">
                    {p.code}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">
                      {p.discount_type === "percent" ? `${p.value}% off` : `$${(p.value / 100).toFixed(0)} off`}
                    </p>
                    <p className="text-xs text-neutral-400 font-medium mt-0.5">
                      Used {p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""} times
                    </p>
                  </div>
                  <button
                    data-testid={`promo-toggle-${p.code}`}
                    onClick={() => togglePromo(p)}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                      p.active
                        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                    }`}
                  >
                    {p.active ? "Active" : "Off"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="mt-6" data-testid="gallery-manager">
            <div className="card-soft p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <span className="w-12 h-12 rounded-2xl bg-[#eac119]/25 flex items-center justify-center shrink-0">
                <Images size={20} className="text-[#1d1d1b]" />
              </span>
              <div className="flex-1">
                <h3 className="font-display font-extrabold tracking-tight">Gallery photos</h3>
                <p className="text-sm text-neutral-500 font-medium mt-1">
                  Upload photos straight from your device — they appear in the site's Gallery mosaic. JPG, PNG or WebP, up to 10 MB each.
                </p>
              </div>
              <input
                ref={fileInputRef}
                data-testid="gallery-upload-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => uploadPhotos(e.target.files)}
              />
              <button
                data-testid="gallery-upload-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadBusy}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold disabled:opacity-60 shrink-0"
              >
                {uploadBusy ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
                {uploadBusy ? "Uploading…" : "Add photos from device"}
              </button>
            </div>

            {gallery.length === 0 ? (
              <p className="text-neutral-400 text-sm py-10 text-center font-medium">
                No photos uploaded yet — the site shows sample photos until you add your own.
              </p>
            ) : (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((p) => (
                  <div key={p.id} data-testid={`gallery-photo-${p.id}`} className="card-soft overflow-hidden group relative">
                    <img
                      src={fullUrl(p.url)}
                      alt="Gallery upload"
                      className="w-full h-44 object-cover"
                    />
                    <button
                      data-testid={`gallery-delete-${p.id}`}
                      onClick={() => deletePhoto(p)}
                      className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      title="Remove photo"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "videos" && (
          <div className="mt-6" data-testid="videos-manager">
            <div className="card-soft p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <span className="w-12 h-12 rounded-2xl bg-[#c63f3e]/15 flex items-center justify-center shrink-0">
                <Clapperboard size={20} className="text-[#c63f3e]" />
              </span>
              <div className="flex-1">
                <h3 className="font-display font-extrabold tracking-tight">Studio videos</h3>
                <p className="text-sm text-neutral-500 font-medium mt-1">
                  Upload clips of the lounge — they appear in the "Studio videos" section right after the workshop listings, playing silently as guests scroll. MP4, WebM or MOV, up to 150 MB.
                </p>
              </div>
              <input
                ref={videoInputRef}
                data-testid="video-upload-input"
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(e) => uploadVideo(e.target.files?.[0])}
              />
              <button
                data-testid="video-upload-button"
                onClick={() => videoInputRef.current?.click()}
                disabled={videoBusy}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold disabled:opacity-60 shrink-0"
              >
                {videoBusy ? <Loader2 size={15} className="animate-spin" /> : <Clapperboard size={15} />}
                {videoBusy ? "Uploading…" : "Add video from device"}
              </button>
            </div>

            {videos.length === 0 ? (
              <p className="text-neutral-400 text-sm py-10 text-center font-medium">
                No videos yet — the section stays hidden on the site until you add your first clip.
              </p>
            ) : (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {videos.map((v) => (
                  <div key={v.id} data-testid={`admin-video-${v.id}`} className="card-soft overflow-hidden group relative bg-black">
                    <video src={fullUrl(v.url)} muted loop playsInline autoPlay className="w-full h-44 object-cover" />
                    <button
                      data-testid={`video-delete-${v.id}`}
                      onClick={() => deleteVideo(v)}
                      className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      title="Remove video"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "content" && contentDraft && (
          <div className="mt-6" data-testid="content-editor">
            <p className="text-sm text-neutral-500 font-medium flex items-center gap-2 mb-5">
              <Type size={15} className="text-[#1d1d1b]" />
              Edit any text on the public site, then hit save once at the bottom.
            </p>
            <div className="grid gap-5">
              {CONTENT_SECTIONS.map((section) => (
                <div key={section.title} className="card-soft p-6">
                  <h3 className="font-display font-extrabold tracking-tight">{section.title}</h3>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {section.fields.map((f) => (
                      <div key={f.key} className={f.multi || f.image ? "sm:col-span-2" : ""}>
                        <label className="block text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 mb-2">
                          {f.label}
                        </label>
                        {f.image ? (
                          <div className="flex items-center gap-3">
                            {(contentDraft[f.key]) && (
                              <img src={contentDraft[f.key]} alt="" className="w-16 h-16 rounded-xl object-cover border border-neutral-200 shrink-0" />
                            )}
                            <input
                              data-testid={`content-field-${f.key}`}
                              value={contentDraft[f.key] ?? ""}
                              onChange={(e) => setContentDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                              className="form-input flex-1"
                              placeholder="Image URL"
                            />
                            <label
                              data-testid={`content-upload-${f.key}`}
                              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#1d1d1b] text-[#eae4da] px-4 py-2.5 text-xs font-bold cursor-pointer hover:bg-black transition-colors"
                            >
                              <ImagePlus size={13} />
                              From device
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={(e) => { uploadFieldImage(f.key, e.target.files?.[0]); e.target.value = ""; }}
                              />
                            </label>
                          </div>
                        ) : f.multi ? (
                          <textarea
                            data-testid={`content-field-${f.key}`}
                            rows={f.rows || 3}
                            value={contentDraft[f.key] ?? ""}
                            onChange={(e) => setContentDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                            className="form-input resize-y"
                          />
                        ) : (
                          <input
                            data-testid={`content-field-${f.key}`}
                            value={contentDraft[f.key] ?? ""}
                            onChange={(e) => setContentDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                            className="form-input"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="sticky bottom-5 mt-6 flex justify-end">
              <button
                data-testid="content-save-button"
                onClick={saveContent}
                disabled={contentBusy}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold shadow-2xl disabled:opacity-60"
              >
                {contentBusy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save site text
              </button>
            </div>
          </div>
        )}
      </main>

      {formOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4" onClick={() => setFormOpen(false)}>
          <motion.form
            data-testid="admin-workshop-form"
            onSubmit={saveWorkshop}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-[28px] bg-white p-7 shadow-2xl"
          >
            <h2 className="font-display font-extrabold text-2xl tracking-tight">
              {editing ? "Edit workshop" : "New workshop"}
            </h2>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title" className="sm:col-span-2">
                <input data-testid="workshop-form-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="form-input" placeholder="Glow-in-the-Dark Paint Night" />
              </Field>
              <Field label="Category">
                <select data-testid="workshop-form-theme" value={form.theme} onChange={(e) => setForm((f) => ({ ...f, theme: e.target.value }))} className="form-input">
                  {(categories.some((c) => c.name === form.theme)
                    ? categories
                    : [...categories, { id: "current", name: form.theme }]
                  ).map((c) => (
                    <option key={c.id} value={c.name} className="bg-white">{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Price per seat (USD)">
                <input data-testid="workshop-form-price" required type="number" min="1" step="1" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="form-input" />
              </Field>
              <Field label="Date" className="sm:col-span-2">
                <input data-testid="workshop-form-date" required type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="form-input" />
              </Field>
            </div>

            <div className="mt-5">
              <label className="block text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 mb-2">
                Timeslots — each with its own capacity
              </label>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5 px-1">
                  <span className="flex-1 text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400">Time (type any, e.g. 6:30 PM)</span>
                  <span className="w-24 shrink-0 text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400">Seats</span>
                  <span className="w-11 shrink-0" />
                </div>
                {form.timeslots.map((s, i) => (
                  <div key={i} data-testid={`workshop-form-timeslot-${i}`} className="flex items-center gap-2.5">
                    <input
                      data-testid={`workshop-form-timeslot-label-${i}`}
                      required
                      value={s.label}
                      onChange={(e) => setSlot(i, "label", e.target.value)}
                      placeholder="6:30 PM"
                      className="form-input flex-1"
                      style={{ width: "auto" }}
                    />
                    <input
                      data-testid={`workshop-form-timeslot-capacity-${i}`}
                      required
                      type="number"
                      min="1"
                      value={s.capacity}
                      onChange={(e) => setSlot(i, "capacity", e.target.value)}
                      placeholder="5"
                      className="form-input shrink-0"
                      style={{ width: "6rem" }}
                    />
                    <button
                      type="button"
                      data-testid={`workshop-form-timeslot-remove-${i}`}
                      onClick={() => setForm((f) => ({ ...f, timeslots: f.timeslots.filter((_, idx) => idx !== i) }))}
                      disabled={form.timeslots.length <= 1}
                      className="w-11 h-11 rounded-2xl border border-neutral-200 flex items-center justify-center hover:border-red-400 hover:text-red-500 transition-colors disabled:opacity-30 shrink-0"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                data-testid="workshop-form-add-timeslot"
                onClick={() => setForm((f) => ({ ...f, timeslots: [...f.timeslots, { id: null, label: "", capacity: "5" }] }))}
                disabled={form.timeslots.length >= 8}
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-neutral-200 px-4 py-2 text-xs font-bold hover:border-black transition-colors disabled:opacity-40"
              >
                <Plus size={13} /> Add timeslot
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4">
              <Field label="Cover photo (blank = category default)">
                <div className="flex items-center gap-3">
                  {form.image && (
                    <img src={form.image} alt="" className="w-14 h-14 rounded-xl object-cover border border-neutral-200 shrink-0" />
                  )}
                  <input data-testid="workshop-form-image" value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} className="form-input flex-1" placeholder="https://…" />
                  <label
                    data-testid="workshop-form-image-upload"
                    className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#1d1d1b] text-[#eae4da] px-4 py-2.5 text-xs font-bold cursor-pointer hover:bg-black transition-colors"
                  >
                    <ImagePlus size={13} />
                    From device
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => { uploadWorkshopImage(e.target.files?.[0]); e.target.value = ""; }}
                    />
                  </label>
                </div>
              </Field>
              <Field label="Listing photos & video (first one is the cover — video covers play silently as guests scroll)">
                <div className="rounded-2xl border border-neutral-200 p-3.5">
                  {form.media.length > 0 && (
                    <div className="grid grid-cols-4 gap-2.5 mb-3">
                      {form.media.map((m, i) => (
                        <div
                          key={`${m.url}-${i}`}
                          data-testid={`workshop-form-media-item-${i}`}
                          className={`relative rounded-xl overflow-hidden bg-black aspect-square group ${i === 0 ? "ring-2 ring-[#eac119]" : ""}`}
                        >
                          {m.kind === "video" ? (
                            <video src={m.url} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                          ) : (
                            <img src={m.url} alt="" className="w-full h-full object-cover" />
                          )}
                          {i === 0 && (
                            <span className="absolute top-1.5 left-1.5 rounded-full bg-[#eac119] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide">
                              Cover
                            </span>
                          )}
                          {m.kind === "video" && (
                            <span className="absolute bottom-1.5 left-1.5 rounded-full bg-black/70 text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide">
                              Video
                            </span>
                          )}
                          <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {i !== 0 && (
                              <button
                                type="button"
                                data-testid={`workshop-form-media-cover-${i}`}
                                onClick={() => setMediaCover(i)}
                                title="Make cover"
                                className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-[#1d1d1b] shadow"
                              >
                                <Star size={12} />
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`workshop-form-media-remove-${i}`}
                              onClick={() => removeMedia(i)}
                              title="Remove"
                              className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-red-500 shadow"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <label
                      data-testid="workshop-form-media-add-photos"
                      className={`inline-flex items-center gap-2 rounded-full border border-neutral-200 px-4 py-2.5 text-xs font-bold cursor-pointer hover:border-black transition-colors ${mediaBusy || form.media.length >= 8 ? "opacity-40 pointer-events-none" : ""}`}
                    >
                      {mediaBusy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                      Add photos
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        className="hidden"
                        onChange={(e) => { uploadWorkshopMedia(e.target.files); e.target.value = ""; }}
                      />
                    </label>
                    <label
                      data-testid="workshop-form-media-add-video"
                      className={`inline-flex items-center gap-2 rounded-full border border-neutral-200 px-4 py-2.5 text-xs font-bold cursor-pointer hover:border-black transition-colors ${mediaBusy || form.media.length >= 8 ? "opacity-40 pointer-events-none" : ""}`}
                    >
                      {mediaBusy ? <Loader2 size={13} className="animate-spin" /> : <Clapperboard size={13} />}
                      Add video
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        className="hidden"
                        onChange={(e) => { uploadWorkshopMedia(e.target.files); e.target.value = ""; }}
                      />
                    </label>
                  </div>
                  <p className="text-[11px] text-neutral-400 font-medium mt-2.5">
                    Guests tap the listing to flip through these. Use the star to make one the cover. The photo above stays as the backup photo for emails and tickets.
                  </p>
                </div>
              </Field>
              <Field label="Description">
                <textarea data-testid="workshop-form-description" rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="form-input resize-none" placeholder="What the night feels like…" />
              </Field>
            </div>

            {!editing && (
              <div className="mt-5 rounded-2xl border border-neutral-200 bg-[#eae4da]/50 p-4">
                <label className="flex items-center gap-3 text-sm font-bold">
                  <input
                    type="checkbox"
                    data-testid="admin-recurring-toggle"
                    checked={form.recurring !== "none"}
                    onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.checked ? "weekly" : "none" }))}
                    className="w-4 h-4 accent-[#1d1d1b]"
                  />
                  Make this a recurring event
                </label>
                {form.recurring !== "none" && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <Field label="Repeats">
                      <select data-testid="workshop-form-recurring" value={form.recurring} onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.value }))} className="form-input">
                        <option value="weekly" className="bg-white">Every week</option>
                        <option value="biweekly" className="bg-white">Every 2 weeks</option>
                        <option value="monthly" className="bg-white">Every 4 weeks</option>
                      </select>
                    </Field>
                    <Field label="Extra sessions">
                      <input data-testid="workshop-form-repeat-count" type="number" min="0" max="52" value={form.repeat_count} onChange={(e) => setForm((f) => ({ ...f, repeat_count: e.target.value }))} className="form-input" />
                    </Field>
                  </div>
                )}
              </div>
            )}

            {formError && <p className="mt-4 text-sm font-semibold text-red-600">{formError}</p>}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="flex-1 rounded-full border border-neutral-200 py-3 text-sm font-bold hover:border-black transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="workshop-form-submit"
                disabled={formBusy}
                className="btn-primary flex-1 rounded-full disabled:opacity-60 py-3 text-sm font-bold flex items-center justify-center gap-2"
              >
                {formBusy && <Loader2 size={14} className="animate-spin" />}
                {editing ? "Save changes" : "Create workshop"}
              </button>
            </div>
          </motion.form>
        </div>
      )}

      {toast && (
        <motion.div
          data-testid="admin-toast"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] rounded-full bg-[#1d1d1b] text-[#eae4da] px-6 py-3 text-sm font-bold shadow-2xl"
        >
          {toast}
        </motion.div>
      )}

      <style>{`
        .form-input {
          width: 100%;
          border-radius: 1rem;
          border: 1px solid #ddd6c9;
          background: #ffffff;
          padding: 0.7rem 1rem;
          font-size: 0.875rem;
          outline: none;
          font-weight: 500;
        }
        .form-input:focus { border-color: #1d1d1b; }
        .form-input::placeholder { color: #c7c2b6; }
      `}</style>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 mb-2">
      {label}
    </label>
    {children}
  </div>
);
